import React, { useState, useEffect } from "react";
import { useUser } from "./context/UserContext";
import { db, auth } from "./firebaseConfig";
import { collection, onSnapshot } from "firebase/firestore";
import { signOut } from "firebase/auth";
import VehiclesList from "./components/VehiclesList";
import MapComponent from "./components/MapComponent";
import CSVUpload from "./components/CSVUpload";
import AdminCreateUser from "./components/AdminCreateUser";
import BillingOverview from "./components/BillingOverview";
import SupportForm from "./components/SupportForm";
import AlertsDropdown from "./components/AlertsDropdown";
import TripHistoryModal from "./components/TripHistoryModal";
import AdminTable from "./components/AdminTable";
import "./styles/Dashboard.css";

const Dashboard = () => {
  const { user, tier, loading, checkFeatureAccess, restrictTripHistory } = useUser();
  const [vehicles, setVehicles] = useState([]);
  const [userVehicles, setUserVehicles] = useState([]);
  const [selectedVehicles, setSelectedVehicles] = useState([]);
  const [showTripModal, setShowTripModal] = useState(false);
  const [showAdminModal, setShowAdminModal] = useState(false);

  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, "vehicles"), (snapshot) => {
      const fetched = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setVehicles(fetched);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!user) {
      setUserVehicles([]);
      return;
    }
    if (tier === "admin") {
      setUserVehicles(vehicles);
    } else {
      const owned = vehicles.filter((v) => v.ownerUid === user.uid);
      setUserVehicles(owned);
    }
  }, [vehicles, user, tier]);

  const visibleVehicles = tier === "admin" ? vehicles : userVehicles;

  const handleLogout = async () => {
    try {
      await signOut(auth);
      window.location.href = "/";
    } catch (error) {
      console.error("Logout error:", error);
      window.location.href = "/";
    }
  };

  const handleVehicleSelection = (ids) => setSelectedVehicles(ids);

  if (loading) return <div>Loading...</div>;
  if (!user) return <div>Please log in. <a href="/login">Login</a></div>;

  return (
    <div className="dashboard">
      <div className="top-nav">
        <h1>🚗 TrackSmart Solutions</h1>
        <div className="top-actions">
          {checkFeatureAccess("billingInfo") ? (
            <BillingOverview />
          ) : (
            <div className="restricted-feature">
              Billing: <a href="/pricing">Upgrade</a>
            </div>
          )}
          {checkFeatureAccess("support") ? (
            <SupportForm />
          ) : (
            <div className="restricted-feature">
              Support: <a href="/pricing">Upgrade</a>
            </div>
          )}
          {checkFeatureAccess("notifications") ? (
            <AlertsDropdown />
          ) : (
            <div className="restricted-feature">
              Notifications: <a href="/pricing">Upgrade to Standard</a>
            </div>
          )}
          {checkFeatureAccess("tripHistory") ? (
            <button className="btn dark" onClick={() => setShowTripModal(true)}>
              📍 Trip History
            </button>
          ) : (
            <div className="restricted-feature">
              Trip History: <a href="/pricing">Upgrade to Standard</a>
            </div>
          )}
          {checkFeatureAccess("csvUpload") && <CSVUpload />}
          {checkFeatureAccess("createUser") && <AdminCreateUser />}
          {checkFeatureAccess("createUser") && (
            <button
              className="btn dark"
              onClick={() => setShowAdminModal(true)}
            >
              🛠 Admin Dashboard
            </button>
          )}
          <button className="btn logout" onClick={handleLogout}>
            🚪 Logout
          </button>
        </div>
      </div>
      <div className="main-content">
        <aside className="sidebar">
          <VehiclesList
            vehicles={visibleVehicles}
            onVehicleSelection={handleVehicleSelection}
          />
        </aside>
        <section className="map-view">
          <MapComponent
            vehicles={visibleVehicles}
            selectedVehicles={selectedVehicles}
          />
        </section>
      </div>
      {showTripModal && checkFeatureAccess("tripHistory") && (
        <TripHistoryModal
          currentUser={{ uid: user.uid, tier }}
          restrictTripHistory={restrictTripHistory}
          onClose={() => setShowTripModal(false)}
        />
      )}
      {showAdminModal && checkFeatureAccess("createUser") && (
        <div
          className="admin-modal-overlay"
          onClick={() => setShowAdminModal(false)}
        >
          <div
            className="admin-tools admin-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="close-modal-button"
              onClick={() => setShowAdminModal(false)}
            >
              ×
            </button>
            <AdminTable />
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;